import { useMemo } from 'react';
import type { GuestFormOptions } from '@peles/shared';
import { CsvImportWizard, type CsvImportReview } from '../common';
import { guestImportConfig } from '../../features/guests/guestImportConfig';
import { useCommitEventGuestImport, usePreviewEventGuestImport } from '../../hooks/useEventGuests';

export interface GuestImportWizardProps {
  open: boolean;
  onClose: () => void;
  eventId: string;
  options?: GuestFormOptions;
}

const REASON_LABELS: Record<string, string> = {
  email: 'same email',
  phone: 'same phone',
  name: 'same name',
};

/**
 * Import guests from a file or a pasted list, checked against the event's
 * guest list first: a row that looks like a guest already there (same email,
 * phone or name) — or like an earlier row of the same list — is shown, and
 * each can be skipped, merged into that guest or added anyway.
 */
export function GuestImportWizard({ open, onClose, eventId }: GuestImportWizardProps) {
  const preview = usePreviewEventGuestImport(eventId);
  const commit = useCommitEventGuestImport(eventId);
  const { mutateAsync: previewAsync } = preview;
  const { mutateAsync: commitAsync } = commit;

  const review = useMemo<CsvImportReview>(
    () => ({
      preview: async (rows) => {
        const result = await previewAsync(rows);
        return result.rows.map((row) => ({
          index: row.index,
          errors: row.errors,
          ...(row.duplicate
            ? {
                duplicate: {
                  id: row.duplicate.guestId,
                  label: row.duplicate.name,
                  reasons: row.duplicate.reasons.map((r) => REASON_LABELS[r] ?? r),
                },
              }
            : {}),
          ...(row.duplicateOfRow !== undefined ? { duplicateOfRow: row.duplicateOfRow } : {}),
        }));
      },
      commit: (rows) => commitAsync(rows),
    }),
    [previewAsync, commitAsync],
  );

  return (
    <CsvImportWizard
      open={open}
      onClose={onClose}
      config={{ ...guestImportConfig, title: 'Import guests' }}
      scope={{ eventId }}
      review={review}
    />
  );
}
